import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "wekit.schools.presenter";

/**
 * Presenter Mode — hides sidebar + header chrome for a distraction-free
 * full-screen demo. Persists across reloads so a mid-demo refresh doesn't
 * eject the presenter back into the admin shell.
 *
 * Global shortcut: Shift+P toggles. Esc exits.
 */
export function usePresenterMode() {
  const [on, setOn] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(STORAGE_KEY) === "1";
  });

  const set = useCallback((v: boolean) => {
    setOn(v);
    try {
      window.localStorage.setItem(STORAGE_KEY, v ? "1" : "0");
    } catch {
      /* storage unavailable — mode still applies for the session */
    }
  }, []);

  const toggle = useCallback(() => set(!on), [on, set]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Ignore when typing in inputs / editable fields.
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) return;

      if (e.shiftKey && (e.key === "P" || e.key === "p") && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        set(!on);
      } else if (e.key === "Escape" && on) {
        set(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [on, set]);

  return { on, toggle, set };
}
